import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { DraftingCompass } from 'lucide-react';

/**
 * Animaciones
 */
const shimmerAnimation = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

/**
 * Styled Components
 */
const MessageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  maxWidth: '100%',
  marginBottom: '12px',
  alignSelf: 'flex-start',
  pointerEvents: 'auto',
  zIndex: 10,
  position: 'relative'
});

const MessageBubble = styled(Box)(({ theme, colors }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  backgroundColor: 'transparent',
  color: colors.textDefault,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: 'auto'
}));

const StyledText = styled(Typography)(({ theme, colors }) => ({
  background: `linear-gradient(
    90deg,
    ${colors.textDefault} 0%,
    ${colors.textSecondary} 50%,
    ${colors.textDefault} 100%
  )`,
  backgroundSize: '200% auto',
  color: 'transparent',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  animation: `${shimmerAnimation} 2s linear infinite`,
  fontWeight: 400,
}));

// Contenedor para el ícono
const IconWrapper = styled(Box)({
  marginLeft: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// Estilizamos directamente el ícono SVG en lugar de su contenedor
const StyledCompass = styled(DraftingCompass)(({ colors }) => ({
  stroke: colors.textSecondary, // Color base para el trazo
  animation: `${shimmerAnimation} 2s linear infinite`,
  // Aplicamos una transición de opacidad para simular el efecto shimmer
  '@keyframes opacityPulse': {
    '0%': { opacity: 0.6 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.6 }
  },
}));

/**
 * Componente principal que muestra las frases de "pensamiento"
 */
const ThinkingIcon = ({ startTime, colors }) => {
  
  // Seleccionar frases
  const phrases = useMemo(() => {
    return [
      ['Pensando', 'Analizando tu pregunta'],
      ['Consultando fuentes académicas', 'Revisando bibliografía'],
      ['Elaborando respuesta educativa', 'Preparando explicación'],
    ];
  }, []);

  // Estados
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [currentPhrase, setCurrentPhrase] = useState('');
  const [pauseTimer, setPauseTimer] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  // Inicializar la frase
  useEffect(() => {
    setPhaseIndex(0);
    setPauseTimer(0);
    setIsVisible(true);
    const firstBlock = phrases[0] || [];
    setCurrentPhrase(firstBlock[0] || '');
  }, [startTime, phrases]);

  // Lógica para cambiar las frases
  useEffect(() => {
    const interval = setInterval(() => {
      setPhaseIndex((prevIndex) => {
        if (pauseTimer > 0) {
          setPauseTimer((p) => p - 1);
          return prevIndex;
        }
        const nextIndex = Math.min(prevIndex + 1, phrases.length - 1);
        if (nextIndex < phrases.length - 1) {
          const shouldPause = Math.random() < 0.3;
          if (shouldPause) {
            const randomPause = Math.floor(Math.random() * 3) + 1;
            setPauseTimer(randomPause);
          }
        }
        return nextIndex;
      });
    }, 600);
    return () => clearInterval(interval);
  }, [pauseTimer, phrases]);

  useEffect(() => {
    const currentBlock = phrases[phaseIndex] || [];
    const randomIndex = Math.floor(Math.random() * currentBlock.length);
    const newPhrase = currentBlock[randomIndex] || '';
    setCurrentPhrase(newPhrase);
  }, [phaseIndex, phrases]);

  // Solo renderizar si el componente está visible
  if (!isVisible) return null;

  return (
    <MessageContainer>
      <MessageBubble colors={colors}>
        <StyledText variant="body1" colors={colors}>
          {currentPhrase}
        </StyledText>
        <IconWrapper>
          <StyledCompass size={20} colors={colors} />
        </IconWrapper>
      </MessageBubble>
    </MessageContainer>
  );
};

export default ThinkingIcon;